import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import GridBackground from './GridBackground';
import ScrollToTop from './ScrollToTop';
import './css/AssessmentPage.css';

const steps = [
  {
    number: '01',
    title: 'Fill Out the Intake Form',
    desc: 'Tell us about your business, your team, and where you feel the most friction. Takes under 5 minutes.',
  },
  {
    number: '02',
    title: 'AI-Guided Assessment Call',
    desc: 'Our AI agent walks through your business operations with you — uncovering where time is being lost and where automation fits.',
  },
  {
    number: '03',
    title: 'Receive Your Report in 24 Hours',
    desc: 'A full written report with specific tool recommendations, ROI projections, and a 4-day quick start plan.',
  },
];

const deliverables = [
  {
    icon: '⏱',
    title: 'Hours Reclaimed Weekly',
    desc: 'We calculate exactly how much time you can get back — and what that time is worth to your business.',
  },
  {
    icon: '🛠',
    title: 'Specific Tool Recommendations',
    desc: 'Not a generic list. Every recommendation is matched to your business type, team size, and pain areas.',
  },
  {
    icon: '📋',
    title: '4-Day Quick Start Plan',
    desc: 'A day-by-day action plan you can execute yourself — with setup times, costs, and expected time savings.',
  },
  {
    icon: '📈',
    title: 'ROI Projection',
    desc: 'Monthly net value of implementing the recommendations, minus tool costs — so you can see the return clearly.',
  },
];

export default function AssessmentPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-in').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <GridBackground />
      <Navigation />

      {/* Hero */}
      <section className="ap-hero">
        <div className="container">
          <div className="ap-hero-content fade-in">
            <div className="section-tag">// AI Business Assessment</div>
            <h1>Find Out Where Your Business Is Losing Time</h1>
            <p>
              An AI-guided assessment that maps your operations, identifies automation opportunities,
              and delivers a personalized report — in 24 hours.
            </p>
            <div className="ap-hero-cta">
              <Link to="/intake" className="btn btn-primary">Book Your Assessment</Link>
              <a href="https://azurearchprodassess.blob.core.windows.net/public-documents/sample-assessment-report.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                Download Sample Report
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="ap-section" id="how-it-works">
        <div className="container">
          <div className="section-header fade-in">
            <div className="section-tag">// How It Works</div>
            <h2>Simple Process. Immediate Value.</h2>
          </div>
          <div className="ap-steps fade-in">
            {steps.map((s) => (
              <div key={s.number} className="ap-step">
                <div className="ap-step-number">{s.number}</div>
                <h3>{s.number === '01' ? <Link to="/intake">{s.title}</Link> : s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="ap-section ap-section--alt">
        <div className="container">
          <div className="section-header fade-in">
            <div className="section-tag">// Deliverables</div>
            <h2>What's In Your Report</h2>
          </div>
          <div className="ap-deliverables fade-in">
            {deliverables.map((d) => (
              <div key={d.title} className="ap-deliverable">
                <div className="ap-deliverable-icon">{d.icon}</div>
                <h3>{d.title}</h3>
                <p>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Report */}
      <section className="ap-section">
        <div className="container">
          <div className="ap-sample fade-in">
            <div className="ap-sample-text">
              <div className="section-tag">// Sample Report</div>
              <h2>See What You'll Receive</h2>
              <p>
                Every report includes an executive summary, impact-effort matrix, ranked tool
                recommendations, financial ROI breakdown, and a step-by-step quick start plan —
                all tailored to your specific business.
              </p>
              <a href="https://azurearchprodassess.blob.core.windows.net/public-documents/sample-assessment-report.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                Download Sample Report
              </a>
            </div>
            <div className="ap-sample-preview">
              <div className="ap-report-mockup">
                <div className="ap-report-cover">
                  <div className="ap-report-tag">SAMPLE</div>
                  <h4>AI Tools Assessment</h4>
                  <p>Prepared for Smith Realty Group</p>
                  <div className="ap-report-slides">
                    <div className="ap-report-slide">Executive Summary</div>
                    <div className="ap-report-slide">Impact-Effort Matrix</div>
                    <div className="ap-report-slide">Recommended Solutions</div>
                    <div className="ap-report-slide">4-Day Quick Start</div>
                    <div className="ap-report-slide">Financial Impact</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ap-section ap-section--alt">
        <div className="container">
          <div className="ap-final-cta fade-in">
            <h2>Ready to Reclaim Your Time?</h2>
            <p>Fill out the intake form and we'll be in touch to schedule your assessment call.</p>
            <Link to="/intake" className="btn btn-primary">Book Your Assessment</Link>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </>
  );
}
